import {
  SnackbarContent,
  SnackbarProvider as NotistackProvider,
  CustomContentProps,
} from "notistack";
import { Box, styled, Theme, Typography, useMediaQuery } from "@mui/material";
import { Global, css } from "@emotion/react";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import CheckCircleIcon from "@icons/myicons/check-circle-filled.svg?react";
import InfoIcon from "@icons/myicons/info-filled.svg?react";

const StyledIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  color: theme.palette.text.onTooltipPrimary,
  marginRight: theme.spacing(2.5),
  "& path": {
    stroke: theme.palette.background.tooltipPrimary,
  },
}));

const StyledSnackbarContent = styled(SnackbarContent, {
  shouldForwardProp: (prop) =>
    prop !== "isAppBarVisible" &&
    prop !== "autoHideDuration" &&
    prop !== "persist" &&
    prop !== "hideIconVariant" &&
    prop !== "anchorOrigin" &&
    prop !== "iconVariant",
})<{ isAppBarVisible: boolean }>(({ theme, isAppBarVisible }) => ({
  borderRadius: theme.shape.borderRadius,
  flexWrap: "nowrap",
  backgroundColor: theme.palette.background.tooltipPrimary,
  color: theme.palette.text.onTooltipPrimary,
  boxShadow: theme.shadows[1],
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2.5, 4),
  zIndex: 10000,

  "& a": {
    color: theme.palette.text.onTooltipPrimaryLink,
  },

  [theme.breakpoints.down("sm")]: {
    marginBottom: isAppBarVisible ? theme.spacing(11) : theme.spacing(-2),
    maxWidth: "250px",
    alignItems: "flex-start",
  },
}));

interface SnackbarProps extends CustomContentProps {
  isAppBarVisible: boolean;
}

const Snackbar = ({
  ref,
  ...props
}: SnackbarProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { id, variant, message, ...rest } = props;

  return (
    <StyledSnackbarContent
      data-testid={`snackbar-message-${variant}`}
      ref={ref}
      role="alert"
      {...rest}
    >
      {variant === "success" && (
        <StyledIcon>
          <CheckCircleIcon />
        </StyledIcon>
      )}
      {variant === "info" && (
        <StyledIcon>
          <InfoIcon />
        </StyledIcon>
      )}
      <Typography variant="body3">{message}</Typography>
    </StyledSnackbarContent>
  );
};

export const SnackbarProvider = ({ children }: { children?: ReactNode }) => {
  const [isAppBarVisible, setIsAppBarVisible] = useState(false);
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const intersection = useMemo(() => {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) =>
          entry.isIntersecting
            ? setIsAppBarVisible(true)
            : setIsAppBarVisible(false)
        );
      },
      { root: null, threshold: 0.1 }
    );
  }, []);

  useEffect(() => {
    const appBar = document.querySelector("#app-bar");
    if (appBar instanceof Element) {
      intersection.observe(appBar);
    }
  }, [intersection, isLessSm]);

  return (
    <>
      <Global
        styles={css`
          .notistack-SnackbarContainer {
            z-index: 10100 !important;
          }
        `}
      />
      <NotistackProvider
        maxSnack={3}
        Components={{
          success: ({
            ref,
            ...props
          }: SnackbarProps & { ref?: React.Ref<HTMLDivElement> }) => (
            <Snackbar ref={ref} {...props} isAppBarVisible={isAppBarVisible} />
          ),
          info: ({
            ref,
            ...props
          }: SnackbarProps & { ref?: React.Ref<HTMLDivElement> }) => (
            <Snackbar ref={ref} {...props} isAppBarVisible={isAppBarVisible} />
          ),
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isLessSm ? "left" : "center",
        }}
      >
        {children}
      </NotistackProvider>
    </>
  );
};
